import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { authApi } from "../../services/api/authApi";
import { showError, showSuccess } from "../../lib/toast";
import { useAuth } from "../../contexts/AuthContext";
import ScrollToTop from "../../components/ScrollToTop";
import * as yup from "yup";

const Login: React.FC = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const isDigivision = searchParams.get('isDigivision') === 'true';
  
  const loginSchema = yup.object({
    email: yup
      .string()
      .required("Email is required")
      .email("Email is invalid"),
    password: yup
      .string()
      .required("Password is required")
      .min(6, "Password must be at least 6 characters"),
    rememberMe: yup.boolean().optional(),
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // live-clear field error on change
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await loginSchema.validate(formData, { abortEarly: false });
      setErrors({});
    } catch (validationError: any) {
      const fieldErrors: { [key: string]: string } = {};
      if (validationError.inner?.length) {
        validationError.inner.forEach((err: any) => {
          if (err.path && !fieldErrors[err.path]) fieldErrors[err.path] = err.message;
        });
      } else if (validationError.path) {
        fieldErrors[validationError.path] = validationError.message;
      }
      setErrors(fieldErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await authApi.login({
        email: formData.email,
        password: formData.password,
        isDigivision: isDigivision,
      });

      if (response.data) {
        const { user, tokens } = response.data;

        // Save token and user data using AuthContext
        login(tokens.accessToken, user);

        // Redirect based on user role or default to home
        let redirectPath = user.role === "ADMIN" ? "/admin/dashboard" : "/";
        
        // If user came from DIGIvison collection, redirect them there after login
        if (isDigivision) {
          redirectPath = "/products?collection=DigiVision";
        }
        
        navigate(redirectPath);
        showSuccess("Successfully logged in!");
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "Login failed. Please check your credentials.";
      showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNavClick = () => {
    navigate(isDigivision ? '/signup?isDigivision=true' : '/signup');
  };

  return (
    <div className=" d-flex flex-column mb-5" style={{height:'100%'}}>
      <ScrollToTop />
      {/* Breadcrumb */}
      <div className="py-3">
        <div className="container">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0 font_two">
              <li className="breadcrumb-item">
                <a href="/" className="text-decoration-none text-dark">
                  Home
                </a>
              </li>
              <li
                className="breadcrumb-item active fw-bold"
                aria-current="page"
              >
                Sign In
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="container flex-grow-1 d-flex flex-column flex-md-row  py-1">
        {/* Left Content */}

        {/* Left Content */}
        <div className="col-md-6 mb-3" style={{height:'100%'}}>
          <h4 className="mb-2 display-6">Login</h4>
          <p className="text-muted mb-3" style={{fontFamily: 'Satoshi, sans-serif'}}>
            Welcome back! Log in to continue your creative journey.
          </p>
          <span
          onClick={()=>{
            handleNavClick()
          }}
            className="text-link"
          style={{cursor:'pointer',
            textUnderlinePosition: 'from-font'
          }}
          >
            New here? Create an account
          </span>
        </div>

        {/* Right Form */}
        <div className="col-md-6" style={{height:'100%'}}>
          <form
            onSubmit={handleSubmit}
            className="bg-white p-4 rounded-3 shadow-sm"
          >
            <div className="mb-4">
              <label htmlFor="email" className="form-label fw-medium">
                Email Address <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                // required
                className={`form-control form-control-sm custom-input ${errors.email ? "is-invalid" : ""}`}
              />
              {errors.email && (
                <div className="font_two invalid-feedback d-block">{errors.email}</div>
              )}
            </div>

            <div className="mb-2">
              <div className="d-flex justify-content-between align-items-center">
                <label htmlFor="password" className="form-label fw-medium mb-0">
                  Password <span className="text-danger">*</span>
                </label>
               
              </div>
            </div>
            <div className="input-group">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleChange}
                // required
                className={`form-control form-control-sm custom-input ${errors.password ? "is-invalid" : ""}`}
              />
              
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? "👁️" : "👁️‍🗨️"}
              </button>
                {errors.password && (
                  <div className="font_two invalid-feedback d-block">{errors.password}</div>
                )}
            </div>
            <div style={{
              color:'#112C3B',
              display: 'flex',
              width: '100%',
              justifyContent: 'end',
              
            }}>
              <Link
                to="/forgot-password"
                className="text-decoration-none small text-link mt-2"
                style={{ borderBottomWidth: '3px',
                  borderBottomStyle: 'ridge',
                  borderBottomColor: '#ffffff',
                  paddingBottom: '2px',
                  display: 'inline-block' }}
              >
                Forgot Password?
              </Link>
            </div>
            <div className="text-center pt-4">
              <button
                type="submit"
                className="btn btn-dark primary-btn w-50 rounded-pill"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Signing in...
                  </>
                ) : (
                  "Login"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
